"use client";

import React, { FC, memo, useMemo } from "react";
import { cn } from "utils";
import { AssetsListProps } from "./types";
import { AssetsItem } from "./AssetsItem";
import CheckBox from "@/src/components/CheckBox";

/**
 * AssetsList 列表容器组件
 * 显示一个分类的资产列表，支持展开/折叠和选择功能
 *
 * @param props AssetsListProps
 * @returns React.ReactElement
 */
export const AssetsList: FC<AssetsListProps> = memo(
  ({
    title,
    category,
    assets,
    selectedIds,
    onSelectionChange,
    className,
    type = "normal",
  }) => {
    /**
     * 计算是否全选
     */
    const isAllSelected = useMemo(() => {
      if (assets.length === 0) {
        return false;
      }
      return assets.every((asset) => selectedIds.includes(asset.id));
    }, [assets, selectedIds]);

    const hasAssets = assets.length > 0;
    const isIndeterminate =
      hasAssets && selectedIds.length > 0 && !isAllSelected;

    /**
     * 处理全选/反选点击
     */
    const handleSelectAllClick = () => {
      const assetIds = assets.map((asset) => asset.id);
      const nextSelectedIds = isAllSelected ? [] : assetIds;
      onSelectionChange(category, assetIds[0] ?? "", nextSelectedIds);
    };

    /**
     * 处理单个资产选择变化
     */
    const handleItemSelectionChange = (
      assetId: string,
      isSelected: boolean,
    ) => {
      const newSelectedIds = isSelected
        ? Array.from(new Set([...selectedIds, assetId]))
        : selectedIds.filter((id) => id !== assetId);
      onSelectionChange(category, assetId, newSelectedIds);
    };

    return (
      <div
        className={cn(
          "flex-1 bg-background_white_overlay5 rounded-2 px-4 py-3 backdrop-blur-[6px]",
          className,
        )}
        data-tracking-scope="assets-list"
      >
        {/* 标题区域 */}
        <div className="w-full flex items-center gap-2 pb-3 border-b border-border_white_transparent10">
          {/* 全选复选框 */}
          <CheckBox
            data-openreplay-label="AddAssets · AssetsList · CheckBox"
            checked={isAllSelected}
            indeterminate={isIndeterminate}
            onClick={handleSelectAllClick}
            classNames="w-4 h-4"
            iconSize={16}
          />
          <span className="body_medium_m text-content_primary">{title}</span>
        </div>

        {/* 列表容器 */}
        <div
          data-testid="assets-list-container"
          className={type === "modal" ? "pt-[11px]" : "pt-3"}
        >
          {assets.map((asset) => (
            <div key={asset.id} data-testid="asset-item">
              <AssetsItem
                type={type}
                asset={asset}
                isSelected={selectedIds.includes(asset.id)}
                onSelectionChange={handleItemSelectionChange}
              />
            </div>
          ))}
        </div>
      </div>
    );
  },
);

AssetsList.displayName = "AssetsList";
